import { EventLinkAndUnlinkView, EventResource, EventUnlinkView } from '@mark43/cad-api';
import { useMutation } from '@tanstack/react-query';
import { useApiClient } from '~/api-client';

type MergeEventResult = { success: boolean; error: string | null };
type EventLinkView = { primaryEventId: number; childAgencyEventIds: number[] };

interface MergeEventsApi {
    mergeEvents: (args: EventLinkView) => Promise<MergeEventResult>;
    unmergeEvents: (args: EventUnlinkView) => Promise<MergeEventResult>;
    updateEvents: (args: EventLinkAndUnlinkView) => Promise<MergeEventResult>;
}

export function useMergeEventApi(): MergeEventsApi {
    const apiClient = useApiClient();

    const mergeEventMutation = useMutation(
        ({
            primaryEventId,
            childAgencyEventIds,
        }: {
            primaryEventId: number;
            childAgencyEventIds: number[];
        }) =>
            apiClient.request<EventResource.LinkEvents>({
                method: 'POST',
                url: `event/link/${primaryEventId}/`,
                data: childAgencyEventIds,
            })
    );

    const unmergeEventMutation = useMutation((args: EventUnlinkView) =>
        apiClient.request<EventResource.UnlinkEvents>({
            data: args,
            method: 'POST',
            url: 'event/unlink/',
        })
    );

    const updateEventMutation = useMutation((args: EventLinkAndUnlinkView) =>
        apiClient.request<EventResource.CombinedLinkUnlinkEventView>({
            data: args,
            method: 'POST',
            url: 'event/link_and_unlink/',
        })
    );

    async function mergeEvents(args: EventLinkView): Promise<MergeEventResult> {
        try {
            await mergeEventMutation.mutateAsync(args);
            return { success: true, error: null };
        } catch (error) {
            return { success: false, error: (error as Error)?.message };
        }
    }

    async function unmergeEvents(args: EventUnlinkView): Promise<MergeEventResult> {
        try {
            await unmergeEventMutation.mutateAsync(args);
            return { success: true, error: null };
        } catch (error) {
            return { success: false, error: (error as Error)?.message };
        }
    }

    async function updateEvents(args: EventLinkAndUnlinkView): Promise<MergeEventResult> {
        try {
            await updateEventMutation.mutateAsync(args);
            return { success: true, error: null };
        } catch (error) {
            return { success: false, error: (error as Error)?.message };
        }
    }

    return {
        mergeEvents,
        unmergeEvents,
        updateEvents,
    };
}
